<template>
  <base-card-v2 :id="id" :default-class="false">
    <base-card-header :title="personalData.name || $t('base-management.person.empty.data')" icon="icon-customer" />
    <div class="row">
      <div class="col-6 pr-3">
        <ul>
          <li>
            <i class="icon icon-money"></i>
            <span v-if="personalData.averageOrderValue">
              {{ $t('base-management.customers.avg-ticket') }}
              <b>{{ formatCurrency(personalData.averageOrderValue) }}</b>
            </span>
            <span v-else class="empty">{{ $t('base-management.person.empty.data') }}</span>
          </li>
          <li>
            <i class="icon icon-label-18px"></i>
            <div v-if="personalData.segmentId" class="d-flex align-items-center">
              <span class="pr-2">{{ $t('base-management.customer-view.segment') }}</span>
              <segment-bubble :segment-i-d="personalData.segmentId" />
            </div>
            <span v-else class="empty">{{ $t('base-management.person.empty.segment') }}</span>
          </li>
          <li>
            <i class="icon icon-box"></i>
            <span v-if="personalData.productsPerOrder">
              {{ $t('base-management.customer-view.buys') }}
              <b>{{ $_formatDecimal(personalData.productsPerOrder) }}</b>
              {{ $t('base-management.customer-view.products-on-avg') }}
            </span>
            <span v-else class="empty">{{ $t('base-management.person.empty.data') }}</span>
          </li>
          <li>
            <i class="icon icon-in-store"></i>
            <span v-if="personalData.preferredStore">
              {{ $tc('base-management.customer-view.store', 1) }} <b>{{ personalData.preferredStore }}</b>
            </span>
            <span v-else class="empty">{{ $t('base-management.person.empty.data') }}</span>
          </li>
          <li>
            <i class="icon icon-in-store"></i>
            <span v-if="personalData.preferredSeller && personalData.preferredSeller.name">
              {{ $t('base-management.customer-view.salesman') }}
              <b>{{ personalData.preferredSeller.name }}</b>
            </span>
            <span v-else class="empty">{{ $t('base-management.person.empty.data') }}</span>
          </li>
        </ul>
      </div>
      <div class="col-6 pl-3">
        <ul>
          <li>
            <i class="icon icon-calendar-18px"></i>
            <span v-if="createDate"
              >{{ $t('base-management.customer-view.customer-since') }} <b>{{ createDate }}</b></span
            >
            <span v-else class="empty">{{ $t('base-management.person.empty.data') }}</span>
          </li>
          <li>
            <i class="icon icon-birthday-18px"></i>
            <div v-if="personalData.age">
              {{ personalData.age }} {{ $tc('base-management.customer-view.year', personalData.age) }}
            </div>
            <span v-else class="empty">{{ $t('base-management.person.empty.birth') }}</span>
          </li>
          <li>
            <i class="icon icon-map-marker-18px"></i>
            <span v-if="personalData.address && personalData.address.city && personalData.address.state"
              >{{ personalData.address.city }}, {{ personalData.address.state }}</span
            >
            <span v-else class="empty">{{ $t('base-management.person.empty.address') }}</span>
          </li>
          <li>
            <i class="icon icon-phone-18px"></i>
            <a v-if="hasPhones" href="#" class="see-phones" @click="openPhoneLightbox">
              {{ $t('base-management.person.see-phones') }}
            </a>
            <span v-else class="empty">{{ $t('base-management.person.empty.phone') }}</span>
          </li>

          <li>
            <div class="d-flex align-items-center justify-content-between w-100">
              <div class="email-side">
                <i :class="['icon icon-email-18px', { 'optout-color': personalData.optin }]"></i>
                <!-- used to copy email -->
                <input v-if="email" id="customer-email" v-model="email" type="text" name="email" readonly />
                <!-- show truncated email -->
                <span
                  v-if="email"
                  v-tooltip="{
                    theme: 'tooltip-blue',
                    content: $t('base-management.person.copy-email'),
                    distance: 8,
                    placement: 'bottom',
                  }"
                  :class="['text-email', { 'optout-color': personalData.optin }]"
                  data-name="customer-email"
                  @click.prevent="copy"
                >
                  {{ email }}
                </span>
                <span v-else class="empty">{{ $t('base-management.person.empty.email') }}</span>
              </div>
              <div class="optout-side" v-if="personalData.optin">
                <span class="text-optout">Opt-out</span>
                <i
                  class="icon icon-question-12px icon-optout"
                  v-tooltip="{
                    theme: 'tooltip-dark',
                    content: optoutTooltipMessage,
                    distance: 8,
                    placement: 'top',
                  }"
                ></i>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </base-card-v2>
</template>

<script>
import moment from 'moment';
import { numberMixin } from '@/helpers/mixins';
import SegmentBubble from '@/components/_atoms/SegmentBubble';
import { openLightbox } from '@/helpers/lightbox';
// helpers
import { dateCompleteVariation, dateDayMonthYear, dateDayMonthYearPT } from '@/utilities/constants';

export default {
  name: 'CustomerViewPersonalData',
  components: {
    SegmentBubble,
  },
  mixins: [numberMixin],
  props: {
    personalData: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      id: 'personal-data',
    };
  },
  computed: {
    createDate() {
      if (moment(this.personalData.sourceCreatedDate).isValid()) {
        return moment(this.personalData.sourceCreatedDate).format(dateCompleteVariation);
      }
      return false;
    },
    hasPhones() {
      return (
        this.personalData &&
        this.personalData.contacts &&
        this.personalData.contacts.phone.value &&
        this.personalData.contacts.sms.value &&
        this.personalData.contacts.whatsapp.value
      );
    },
    email() {
      return this.personalData.contacts.email.value;
    },
    optoutTooltipMessage() {
      /*
       Usando dados mocados, após obter os dados reais:
       const dataOptOut = this.personalData.opt_out_email_at;
       const reasonOptOut = this.personalData.opt_out_email_reason;
       const originOptOut = this.personalData.opt_out_email_origin;
      */
      const dataOptOut = '2025-11-10 11:17:30';
      const reasonOptOut = 'Não tenho mais interesse em receber comunicações da marca';
      const originOptOut = 'link';

      const formattedDate = moment(dataOptOut).format(
        this.$i18n.locale === 'en' ? dateDayMonthYear : dateDayMonthYearPT,
      );

      const response =
        `<strong>${this.$t('360.profile-resume.opt-out-tooltip.date')}</strong> ${formattedDate}<br><br>` +
        `<strong>${this.$t('360.profile-resume.opt-out-tooltip.origin')}</strong> ` +
        `${this.$t('360.profile-resume.opt-out-tooltip.origin-labels.' + originOptOut) || originOptOut}` +
        (originOptOut === 'link'
          ? `<br><br><strong>${this.$t('360.profile-resume.opt-out-tooltip.reason')}</strong> ${reasonOptOut}`
          : '');
      return response;
    },
  },
  methods: {
    copy(event) {
      const { name } = event.target.dataset;

      if (name) {
        const text = document.getElementById(name);

        text.select();
        document.execCommand('copy');
      }
    },
    openPhoneLightbox() {
      openLightbox('lightbox-phones', { contacts: this.personalData.contacts });
    },
  },
};
</script>
<style lang="scss" scoped>
.card .card-title {
  margin-bottom: 30px;
}
#customer-email {
  position: absolute;
  top: -999px;
  text-indent: -9999px;
  background: transparent;
  border: 0;
}
.text-email {
  cursor: pointer;
  width: 120px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.text-optout {
  font-weight: 800;
  overflow: hidden;
  color: #e74c3c;
  padding: 0 5px 0 10px;
}
.icon-optout {
  color: #e74c3c;
  font-weight: 100;
  margin: 0 auto;
  cursor: pointer;
}
.optout-color {
  color: #e74c3c;
}
.email-side {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  margin: 0;
}
.optout-side {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0;
  margin: 0;
}
</style>
